import { CommonModule, formatDate } from '@angular/common';
import { Component } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  DataAnalysisResultPayload,
  DataAnalysisService,
} from './data-analysis.service';
import { ToastService } from '../services/toast.service';

@Component({
  selector: 'app-data-analysis',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatLabel,
    MatHint,
  ],
  templateUrl: './data-analysis.component.html',
  styleUrl: './data-analysis.component.css',
})
export class DataAnalysisComponent {
  fileName: string | null = null;
  memberIds: string[] = [];
  csvError: string | null = null;
  dragActive = false;
  submitting = false;
  analysisResult: DataAnalysisResultPayload | null = null;

  range = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  constructor(
    private dataAnalysisService: DataAnalysisService,
    private toast: ToastService
  ) {}

  get canSubmit(): boolean {
    const start = this.range.value.start;
    const end = this.range.value.end;
    if (!start || !end || this.memberIds.length === 0) {
      return false;
    }
    const s = this.dateOnly(start).getTime();
    const e = this.dateOnly(end).getTime();
    return s <= e;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.consumeFile(file);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.consumeFile(file);
    }
    input.value = '';
  }

  private consumeFile(file: File): void {
    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      this.toast.errorMessage('Please choose a .csv file.');
      return;
    }
    this.fileName = file.name;
    this.csvError = null;
    this.analysisResult = null;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      try {
        this.memberIds = this.parseMemberIdsFromCsv(text);
        if (this.memberIds.length === 0) {
          this.csvError = 'No member IDs found. Use a column named MemberId (or use the first column).';
        }
      } catch {
        this.memberIds = [];
        this.csvError = 'Could not read that file.';
      }
    };
    reader.onerror = () => {
      this.memberIds = [];
      this.csvError = 'Could not read that file.';
    };
    reader.readAsText(file);
  }

  submit(): void {
    if (!this.canSubmit || this.submitting) {
      return;
    }
    const start = this.range.value.start!;
    const end = this.range.value.end!;
    const payload = {
      memberIds: this.memberIds,
      startDate: formatDate(start, 'yyyy-MM-dd', 'en-US'),
      endDate: formatDate(end, 'yyyy-MM-dd', 'en-US'),
    };
    this.submitting = true;
    this.analysisResult = null;
    this.dataAnalysisService.submitRequest(payload).subscribe({
      next: (res) => {
        this.submitting = false;
        this.analysisResult = res?.result ?? null;
        if (this.analysisResult) {
          this.toast.successMessage('Analysis complete.');
        } else {
          this.toast.successMessage('Request completed.');
        }
      },
      error: (err) => {
        this.submitting = false;
        const msg =
          err?.error?.message ??
          err?.error?.detail ??
          err?.message ??
          'Request failed. Please try again.';
        this.toast.errorMessage(
          typeof msg === 'string' ? msg : 'Request failed. Please try again.'
        );
      },
    });
  }

  downloadCurrentResultCsv(): void {
    if (this.analysisResult) {
      this.downloadResultCsv(this.analysisResult);
    }
  }

  /** CSV columns: Name, MemberId, TIR, veryHigh, high, low, veryLow. */
  private buildTirCsv(result: DataAnalysisResultPayload): string {
    const lines: string[] = ['Name,MemberId,TIR,VeryHigh,High,Low,VeryLow'];
    for (const m of result.members ?? []) {
      const name = this.escapeCsvField(m.name ?? '');
      const id = this.escapeCsvField(m.memberId ?? '');
      const tir = m.range?.inRange ?? 0;
      const veryHigh = m.range?.veryHigh ?? 0;
      const high = m.range?.high ?? 0;
      const low = m.range?.low ?? 0;
      const veryLow = m.range?.veryLow ?? 0;
      lines.push(`${name},${id},${tir},${veryHigh},${high},${low},${veryLow}`);
    }
    return lines.join('\r\n');
  }

  private escapeCsvField(value: string): string {
    if (/[",\r\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  private downloadResultCsv(result: DataAnalysisResultPayload): void {
    const csv = '\uFEFF' + this.buildTirCsv(result);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = formatDate(new Date(), 'yyyy-MM-dd-HHmm', 'en-US');
    a.download = `data-analysis-tir-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private dateOnly(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        inQuotes = !inQuotes;
      } else if (c === ',' && !inQuotes) {
        result.push(cur);
        cur = '';
      } else {
        cur += c;
      }
    }
    result.push(cur);
    return result.map((s) => s.trim());
  }

  private normalizeHeader(cell: string): string {
    return cell
      .replace(/^"|"$/g, '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  private parseMemberIdsFromCsv(text: string): string[] {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) {
      return [];
    }

    const firstRow = this.parseCsvLine(lines[0]).map((c) =>
      c.replace(/^"|"$/g, '').trim()
    );
    const normalizedHeaders = firstRow.map((c) => this.normalizeHeader(c));

    let memberColIndex = normalizedHeaders.findIndex((h) =>
      ['memberid', 'member_id', 'id'].includes(h)
    );
    let dataStartLine = 0;

    if (memberColIndex >= 0) {
      dataStartLine = 1;
    } else {
      memberColIndex = 0;
      dataStartLine = 0;
    }

    const ids = new Set<string>();
    for (let i = dataStartLine; i < lines.length; i++) {
      const cells = this.parseCsvLine(lines[i]).map((c) =>
        c.replace(/^"|"$/g, '').trim()
      );
      const raw = cells[memberColIndex] ?? '';
      if (raw.length > 0) {
        ids.add(raw);
      }
    }
    return [...ids];
  }
}
